import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';

export interface OringMarkProps {
  /**
   * Diameter of the ring in pixels. The inner ring is inset 22% and inherits
   * `currentColor`, so set the colour on this element (or an ancestor) via a
   * `text-*` utility — e.g. `text-[var(--clt-skyline-glow)]`.
   */
  size?: number;
  /**
   * Animate the inner ring with the ambient `o-pulse` loop (opacity 0.5 ↔ 0.9,
   * 3s). The keyframe is suppressed under `prefers-reduced-motion`, so the mark
   * settles to a legible static ring. Defaults to `true`.
   */
  pulse?: boolean;
  className?: string;
}

/**
 * OringMark — the LOCI "O" ring, the brand's atomic icon (a circle with a faint
 * inner ring suggesting both a map pin and a memory locus). Mirrors the design
 * system's `.a-o-icon.a-o-icon--ring.a-o-icon--pulse` atom.
 *
 * Pure presentational. Decorative by default (`aria-hidden`) — it always sits
 * beside real text, so it carries no standalone semantics.
 */
export function OringMark({
  size = 30,
  pulse = true,
  className,
}: OringMarkProps): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      data-testid="oring-mark"
      className={cn(
        'relative inline-block shrink-0 rounded-full border-2 border-current',
        className,
      )}
      style={{ width: size, height: size }}
    >
      <span
        className={cn(
          'pointer-events-none absolute inset-[22%] rounded-full border-[1.5px] border-current opacity-50',
          pulse && 'animate-loci-o-pulse motion-reduce:animate-none',
        )}
      />
    </span>
  );
}
